import { CURRENCY_SYMBOLS } from "../../Plans/PlanTemplates/AnimatedPrice";
import { CurrencyPricing } from "./membershipData";

export type SupportedCurrency = "USD" | "AUD" | "CAD" | "GBP";

/**
 * Get the appropriate price for a given currency from pricing object
 */
export function getPriceForCurrency(
  pricing: CurrencyPricing,
  currency: SupportedCurrency = "USD"
): number {
  return pricing[currency] || pricing.USD;
}

/**
 * Format a price with appropriate currency symbol and formatting
 */
export function formatPrice(
  price: number,
  currency: SupportedCurrency = "USD"
): string {
  const symbol = CURRENCY_SYMBOLS[currency] || CURRENCY_SYMBOLS.USD;

  const isWholeNumber = price % 1 === 0;
  const formattedAmount = isWholeNumber ? price.toString() : price.toFixed(2);

  return `${symbol}${formattedAmount}`;
}

/**
 * Get formatted price from pricing object for specific currency
 */
export function getFormattedPrice(
  pricing: CurrencyPricing,
  currency: SupportedCurrency = "USD"
): string {
  const price = getPriceForCurrency(pricing, currency);
  return formatPrice(price, currency);
}

/**
 * Determine if a currency code is supported
 */
export function isSupportedCurrency(currency: string): currency is SupportedCurrency {
  return ["USD", "AUD", "CAD", "GBP"].includes(currency);
}

/**
 * Get supported currency from currencyIso string or default to USD
 * Maps the client's currencyIso to our supported currencies
 */
export function getSupportedCurrency(currencyIso?: string): SupportedCurrency {
  if (!currencyIso) return "USD";

  const upperCurrency = currencyIso.toUpperCase();

  return isSupportedCurrency(upperCurrency) ? upperCurrency : "USD";
}

/**
 * Helper to get currency symbol
 */
export function getCurrencySymbol(currency: SupportedCurrency = "USD"): string {
  return CURRENCY_SYMBOLS[currency] || CURRENCY_SYMBOLS.USD;
}
